import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

// Plano de Saúde — registro da adesão de UM colaborador (tela Plano de Saúde e checklist
// da admissão). Pergunta por e-mail (texto editável, prévia antes de enviar), registra a
// resposta (sim/não) e o anexo: documento de inclusão (sim) ou comprovante da recusa (não).
// O registro completo destrava o item 'Plano de Saúde' do checklist. Backend:
// core/views_plano_saude.py

const API = process.env.REACT_APP_API_BASE;
const URL_P = () => `${API}/api/bporh/plano-saude/`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const erroMsg = (e, padrao) => e.response?.data?.mensagem || padrao;

// soEmail: abre direto o e-mail ao colaborador (botão 'E-mail' da tabela), sem o registro
const PlanoSaudeRegistro = ({ clienteId, colaborador, onFechar, onMudou, onAviso, soEmail = false }) => {
  const [dados, setDados] = useState(null);      // linha do colaborador (situação, resposta, anexos)
  const [ocupado, setOcupado] = useState('');
  const [erro, setErro] = useState('');
  const [email, setEmail] = useState(null);      // {para, assunto, corpo, operador_id, remetentes, modelo_proprio}
  const [previa, setPrevia] = useState(null);
  const [obs, setObs] = useState('');

  const carregar = useCallback(() => {
    axios.get(URL_P(), { params: { cliente_id: clienteId }, headers: headers() })
      .then((r) => {
        const x = (r.data.colaboradores || []).find(c => c.convenia_id === String(colaborador.convenia_id));
        setDados(x || { fora: true });
        if (x) setObs(x.obs || '');
      })
      .catch((e) => setErro(erroMsg(e, 'Não foi possível carregar')));
  }, [clienteId, colaborador.convenia_id]);
  useEffect(() => { carregar(); }, [carregar]);

  const depois = (r) => { onAviso?.(r.data.mensagem); carregar(); onMudou?.(); };
  const falhou = (e, padrao) => setErro(erroMsg(e, padrao));

  const responder = (resposta) => {
    setOcupado('resp'); setErro('');
    axios.post(URL_P(), { acao: 'responder', cliente_id: clienteId, convenia_id: colaborador.convenia_id, resposta, obs },
      { headers: headers() })
      .then(depois).catch((e) => falhou(e, 'Não foi possível registrar')).finally(() => setOcupado(''));
  };
  const anexar = (tipo, arquivo) => {
    if (!arquivo) return;
    const fd = new FormData();
    fd.append('acao', 'anexar'); fd.append('cliente_id', clienteId);
    fd.append('convenia_id', colaborador.convenia_id); fd.append('tipo', tipo); fd.append('arquivo', arquivo);
    setOcupado(`anexo-${tipo}`); setErro('');
    axios.post(URL_P(), fd, { headers: headers(), timeout: 120000 })
      .then(depois).catch((e) => falhou(e, 'Não foi possível anexar')).finally(() => setOcupado(''));
  };
  const removerAnexo = (id) => {
    axios.post(URL_P(), { acao: 'remover_anexo', cliente_id: clienteId, id }, { headers: headers() })
      .then(depois).catch((e) => falhou(e, 'Não foi possível remover'));
  };
  const baixarAnexo = (a) => {
    axios.get(URL_P(), { params: { anexo: a.id }, headers: headers(), responseType: 'blob' })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        window.open(url, '_blank', 'noopener');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch(() => setErro('Não foi possível abrir o anexo'));
  };

  const abrirEmail = () => {
    setOcupado('email'); setErro('');
    axios.get(URL_P(), { params: { cliente_id: clienteId, convenia_id: colaborador.convenia_id, email: 1 }, headers: headers() })
      .then((r) => setEmail({ ...r.data, operador_id: r.data.operador_padrao ? String(r.data.operador_padrao) : '' }))
      .catch((e) => falhou(e, 'Não foi possível montar o e-mail')).finally(() => setOcupado(''));
  };
  const corpoEmail = (acao) => ({ acao, cliente_id: clienteId, convenia_id: colaborador.convenia_id,
    operador_id: email.operador_id, para: email.para, assunto: email.assunto, corpo: email.corpo });
  const verPrevia = () => {
    setOcupado('previa'); setErro('');
    axios.post(URL_P(), corpoEmail('previa'), { headers: headers() })
      .then((r) => setPrevia(r.data)).catch((e) => falhou(e, 'Não foi possível montar a prévia')).finally(() => setOcupado(''));
  };
  const enviar = () => {
    setOcupado('enviar'); setErro('');
    axios.post(URL_P(), corpoEmail('enviar'), { headers: headers(), timeout: 120000 })
      .then((r) => { setPrevia(null); setEmail(null); depois(r); if (soEmail) onFechar(); })
      .catch((e) => falhou(e, 'Não foi possível enviar')).finally(() => setOcupado(''));
  };
  // modo 'só e-mail': monta o e-mail assim que abre
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (soEmail) abrirEmail(); }, [soEmail]);
  const fecharEmail = () => { setEmail(null); setPrevia(null); if (soEmail) onFechar(); };

  const salvarModelo = () => {
    setOcupado('modelo'); setErro('');
    axios.post(URL_P(), corpoEmail('salvar_modelo'), { headers: headers() })
      .then((r) => { setEmail((x) => ({ ...x, modelo_proprio: true })); onAviso?.(r.data.mensagem); })
      .catch((e) => falhou(e, 'Não foi possível salvar o modelo')).finally(() => setOcupado(''));
  };

  const restaurar = () => {
    axios.post(URL_P(), { acao: 'restaurar', cliente_id: clienteId }, { headers: headers() })
      .then(() => abrirEmail()).catch((e) => falhou(e, 'Não foi possível restaurar'));
  };

  const SIT = {
    vencido: ['3 meses sem o registro', '#a11d2b'], pendente: ['Aguardando resposta', '#8a6d00'],
    falta_anexo: ['Falta o anexo', '#8a6d00'], incluido: ['Quer o plano — registrado', '#116b47'],
    recusou: ['Não quer o plano — registrado', '#116b47'],
  };
  const tipoNecessario = dados?.resposta === 'sim' ? 'documento' : (dados?.resposta === 'nao' ? 'recusa' : null);
  const anexosDo = (tipo) => (dados?.anexos || []).filter(a => a.tipo === tipo);

  const Upload = ({ tipo, rotulo }) => (
    <div className="border rounded p-2 mb-2" style={{ background: tipoNecessario === tipo ? '#f8fbff' : '#fff' }}>
      <div className="small fw-semibold mb-1">
        <i className={`fas ${tipo === 'documento' ? 'fa-file-medical' : 'fa-file-circle-xmark'} me-1`}></i>{rotulo}
        {tipoNecessario === tipo && !anexosDo(tipo).length && <span className="text-danger ms-2">obrigatório</span>}
      </div>
      {anexosDo(tipo).map(a => (
        <div key={a.id} className="small d-flex align-items-center gap-2">
          <button type="button" className="btn btn-link btn-sm p-0" onClick={() => baixarAnexo(a)}>
            <i className="fas fa-paperclip me-1"></i>{a.nome}
          </button>
          <span className="text-muted">{a.em} · {a.por}</span>
          <button type="button" className="btn btn-link btn-sm p-0 text-danger ms-auto" onClick={() => removerAnexo(a.id)}>remover</button>
        </div>
      ))}
      <input type="file" className="form-control form-control-sm mt-1" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic"
        disabled={!!ocupado} onChange={(e) => { anexar(tipo, e.target.files?.[0]); e.target.value = ''; }} />
    </div>
  );

  return (
    <>
      {soEmail && !email && (
        <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10050 }}>
          <div className="modal-dialog modal-dialog-centered"><div className="modal-content p-4 text-center">
            {erro ? <><div className="text-danger small mb-2">{erro}</div>
              <button className="btn btn-secondary btn-sm" onClick={onFechar}>Fechar</button></>
              : <span className="text-muted"><i className="fas fa-spinner fa-spin me-2"></i>Montando o e-mail…</span>}
          </div></div>
        </div>
      )}
      {!soEmail && (
      <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10050 }}>
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content shadow-lg">
            <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
              <div>
                <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                  <i className="fas fa-heart-pulse me-2"></i>Plano de saúde — {colaborador.nome}
                </h5>
                <small style={{ color: '#6c757d' }}>
                  {dados && !dados.fora ? `admissão ${dados.admissao} · ${dados.momento === 'admissao' ? 'prazo: na admissão' : `3 meses em ${dados.limite}`} · ${dados.vinculo || ''}` : ''}
                </small>
              </div>
              <button type="button" className="btn-close" onClick={onFechar}></button>
            </div>
            <div className="modal-body">
              {!dados ? <div className="text-muted py-3 text-center"><i className="fas fa-spinner fa-spin me-2"></i>Carregando…</div>
                : dados.fora ? (
                  <div className="alert alert-light border small mb-0">
                    Este colaborador não entra no controle de plano de saúde desta empresa (regra da empresa, vínculo ou filial).
                  </div>
                ) : (
                  <>
                    <div className="mb-3">
                      <span className="badge" style={{ background: `${(SIT[dados.situacao] || ['', '#6c757d'])[1]}1a`, color: (SIT[dados.situacao] || ['', '#6c757d'])[1], border: `1px solid ${(SIT[dados.situacao] || ['', '#6c757d'])[1]}55` }}>
                        {(SIT[dados.situacao] || [dados.situacao])[0]}
                      </span>
                      <span className="small text-muted ms-2">
                        {dados.email_enviado_em ? `e-mail enviado em ${dados.email_enviado_em} por ${dados.email_por || '—'}` : 'e-mail ainda não enviado'}
                      </span>
                    </div>

                    <div className="mb-3">
                      <div className="small fw-semibold mb-1">1) Perguntar ao colaborador</div>
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={abrirEmail} disabled={!!ocupado}>
                        <i className={`fas ${ocupado === 'email' ? 'fa-spinner fa-spin' : 'fa-envelope'} me-1`}></i>E-mail ao colaborador
                      </button>
                      <span className="small text-muted ms-2">ou registre direto abaixo se a resposta veio por outro meio</span>
                    </div>

                    <div className="mb-3">
                      <div className="small fw-semibold mb-1">2) Resposta</div>
                      <div className="d-flex flex-wrap gap-2 align-items-center">
                        <div className="btn-group btn-group-sm">
                          <button type="button" className={`btn btn-sm ${dados.resposta === 'sim' ? 'btn-success' : 'btn-outline-success'}`}
                            disabled={!!ocupado} onClick={() => responder('sim')}>
                            <i className="fas fa-check me-1"></i>Quer o plano
                          </button>
                          <button type="button" className={`btn btn-sm ${dados.resposta === 'nao' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                            disabled={!!ocupado} onClick={() => responder('nao')}>
                            <i className="fas fa-xmark me-1"></i>Não quer
                          </button>
                        </div>
                        {dados.resposta && (
                          <button type="button" className="btn btn-link btn-sm p-0" onClick={() => responder('')} disabled={!!ocupado}>apagar resposta</button>
                        )}
                        {dados.respondido_em && <span className="small text-muted">registrado em {dados.respondido_em} por {dados.respondido_por}</span>}
                      </div>
                      <input className="form-control form-control-sm mt-2" placeholder="Observação (ex.: incluir esposa e filho)"
                        value={obs} onChange={(e) => setObs(e.target.value)} onBlur={() => dados.resposta && obs !== (dados.obs || '') && responder(dados.resposta)} />
                    </div>

                    <div>
                      <div className="small fw-semibold mb-1">3) Comprovante</div>
                      <Upload tipo="documento" rotulo="Documento de inclusão no plano (quer o plano)" />
                      <Upload tipo="recusa" rotulo="Comprovante de que não quer o plano (ex.: print da resposta)" />
                    </div>
                  </>
                )}
              {erro && <div className="alert alert-danger small py-2 mt-2 mb-0">{erro}</div>}
            </div>
            <div className="modal-footer bg-light">
              <span className="me-auto small text-muted">Completo = resposta + o anexo correspondente. Isso libera o item do checklist e tira o colaborador da cobrança.</span>
              <button className="btn btn-secondary" onClick={onFechar}><i className="fas fa-times me-2"></i>Fechar</button>
            </div>
          </div>
        </div>
      </div>
      )}

      {email && (
        <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10060 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}><i className="fas fa-envelope me-2"></i>E-mail ao colaborador</h5>
                <button type="button" className="btn-close" onClick={fecharEmail}></button>
              </div>
              <div className="modal-body">
                <div className="row g-2">
                  <div className="col-md-7">
                    <label className="form-label small fw-semibold">Para</label>
                    <input className={`form-control form-control-sm ${!email.para ? 'border-danger' : ''}`} value={email.para}
                      onChange={(e) => setEmail(s => ({ ...s, para: e.target.value }))} placeholder="e-mail do colaborador" />
                    {!email.para && <div className="small text-danger mt-1">Sem e-mail na Convenia — digite o endereço.</div>}
                  </div>
                  <div className="col-md-5">
                    <label className="form-label small fw-semibold">Enviar como</label>
                    <select className="form-select form-select-sm" value={email.operador_id}
                      onChange={(e) => setEmail(s => ({ ...s, operador_id: e.target.value }))}>
                      <option value="">Escolha…</option>
                      {(email.remetentes || []).map(r => <option key={r.id} value={String(r.id)}>{r.nome}</option>)}
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-semibold">Assunto</label>
                    <input className="form-control form-control-sm" value={email.assunto}
                      onChange={(e) => setEmail(s => ({ ...s, assunto: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-semibold d-flex">
                      Texto
                      <span className="ms-auto fw-normal text-muted small">
                        {email.modelo_proprio ? 'Modelo desta empresa' : 'Modelo padrão'}
                        {email.modelo_proprio && <button type="button" className="btn btn-link btn-sm p-0 ms-2" onClick={restaurar}>restaurar padrão</button>}
                      </span>
                    </label>
                    <textarea className="form-control form-control-sm" rows={10} value={email.corpo}
                      onChange={(e) => setEmail(s => ({ ...s, corpo: e.target.value }))} />
                    <div className="form-text">Use "Salvar como modelo" para guardar o texto desta empresa (também é salvo ao enviar); o nome se ajusta sozinho para cada colaborador.</div>
                  </div>
                </div>
                {previa && (
                  <div className="mt-3">
                    <div className="small border-bottom pb-2 mb-2" style={{ background: '#f8f9fa' }}>
                      <div><b>De:</b> {previa.de}</div><div><b>Para:</b> {previa.para}</div>
                      {previa.cc && <div><b>Cc:</b> {previa.cc}</div>}
                      <div><b>Assunto:</b> {previa.assunto}</div>
                    </div>
                    <iframe title="Prévia" srcDoc={previa.html} sandbox="" style={{ width: '100%', height: 420, border: 0, background: '#f4f6fa' }} />
                  </div>
                )}
                {erro && <div className="alert alert-danger small py-2 mt-2 mb-0">{erro}</div>}
              </div>
              <div className="modal-footer bg-light">
                <button className="btn btn-secondary" onClick={fecharEmail}>Fechar</button>
                <button className="btn btn-outline-primary" onClick={salvarModelo} disabled={!!ocupado}
                  title="Guarda este assunto e texto como modelo da empresa (o nome se ajusta para cada colaborador)">
                  <i className={`fas ${ocupado === 'modelo' ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`}></i>Salvar como modelo
                </button>
                {!previa ? (
                  <button className="btn btn-primary" onClick={verPrevia} disabled={!!ocupado}>
                    <i className={`fas ${ocupado === 'previa' ? 'fa-spinner fa-spin' : 'fa-eye'} me-2`}></i>Ver prévia
                  </button>
                ) : (
                  <>
                    <button className="btn btn-outline-secondary" onClick={() => setPrevia(null)}><i className="fas fa-pen me-2"></i>Editar</button>
                    <button className="btn btn-success" onClick={enviar} disabled={!!ocupado || !email.operador_id}
                      title={!email.operador_id ? 'Escolha quem envia' : 'Enviar ao colaborador'}>
                      <i className={`fas ${ocupado === 'enviar' ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`}></i>Enviar
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PlanoSaudeRegistro;
